import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import * as actions from '~/store/actions';
import useTypedSelector from '~/hooks/useTypedSelector';
import * as NuacomComponent from '~/components';
import * as Loaders from '~/components/Loaders';
import * as lib from '~/dependencies';
import { useTryToCall, useNavigation, useConversation } from '~/hooks';
import { ChannelTypeEnum } from '~/enums/conversations';
import { Message, DraftMessage } from '~/interfaces/conversations';
import { isValidInviteTarget, getChannelIconById, log } from '~/helpers';
import {
  ArrowLeftIcon,
  CallConversationIcon,
  SearchConversationIcon,
} from '~/assets/svgIcons';
import MessageList from './components/List';
import MessageTextInput from '../components/MessageTextInput';
import {
  Container,
  Block,
  Right,
  TitleContent,
  SearchBox,
  ClearSearchBtn,
  SearchInput,
  Loader,
  ResponseWindowClosed,
  ChannelIconStack,
} from './styles';

/**
 * Minimum characters to start searching in messages
 */
export const MIN_SEARCH_CARACTERS = 0;

type MessagesProps = any;

export default function Messages(props: MessagesProps) {
  // HOOK's
  const { t } = lib.useTranslation();
  const call = useTryToCall();
  const navigate = useNavigation();
  const {
    addMessage,
    loading,
    setErrorMessage,
    errorMessage,
  } = useConversation();
  const dispatch = useDispatch();

  // SELECTOR's
  const { chat: activeChat, isLoading } = useTypedSelector(
    state => state.conversation
  );
  const isWhatsappEnabled = useTypedSelector(
    state => state.accountInfo.featureFlags.isWhatsappEnabled
  );

  // STATE's
  const [searchBox, setSearchBox] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  /**
   * This state is used to determine if channelType was set by user or automatically by the last message channel type.
   */
  const [isUserChannelChoose, setIsUserChannelChoose] = useState(false);
  const [channelType, setChannelType] = useState<ChannelTypeEnum>(
    ChannelTypeEnum.SMS
  );

  // CONST's
  const { location } = props;
  const {
    id,
    contactName: navParamsContactName,
    contactNumber: navParamsContactNumber,
  } = location.state || {};
  const {
    messages: allMessages,
    contactName: convContactName,
    contactNumber: convContactNumber,
    expiresAt,
    channels = [],
  } = activeChat || {
    messages: [],
    channels: [] as ChannelTypeEnum[],
  };

  /**
   * This variable is used to set the initial channel type based on the last message channel type,
   * but only if the user hasn't manually selected a channel yet.
   * This way, when the user opens a conversation, the channel used in the last message will be selected by default,
   * but if they choose a different channel, that choice will be respected.
   */
  const lastMessageChannelType:
    | ChannelTypeEnum
    | undefined = allMessages?.length
    ? allMessages[allMessages.length - 1].channelType
    : undefined;

  // This map defines which channels are enabled by feature flags. If a channel is not in the map, it's considered enabled by default.
  const channelFeatureFlagMap: Partial<Record<ChannelTypeEnum, boolean>> = {
    [ChannelTypeEnum.WHATSAPP]: isWhatsappEnabled,
  };

  // Filter channels by feature flags
  const activeChannels = channels.filter(channel => {
    const flag = channelFeatureFlagMap[channel];
    return flag === undefined ? true : flag;
  });

  // Order channels putting the selected one first
  const activeChannelsOrdered = [...activeChannels].sort((a, b) => {
    if (a === channelType) return -1;
    if (b === channelType) return 1;
    return 0;
  });

  const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;

  /**
   * The WhatsApp response window is 24 hours from the last message received.
   * If the conversation is expired and the channel type is WhatsApp,
   * the app consider that the response window is closed.
   */
  const isWhatsAppResponseWindowClose =
    isExpired && channelType === ChannelTypeEnum.WHATSAPP;

  const contactName = navParamsContactName || convContactName;
  const contactNumber = navParamsContactNumber || convContactNumber;
  const displayContactTitle = contactName || contactNumber;

  const messages =
    searchValue.length > MIN_SEARCH_CARACTERS
      ? allMessages.filter((e: Message) =>
          e.message.toLowerCase().includes(searchValue.toLowerCase())
        )
      : allMessages;

  const hasChannels = Array.isArray(channels) && channels.length > 0;

  const handleCallAction = () => {
    if (isValidInviteTarget(contactNumber)) {
      navigate.displayCall();
      call(contactNumber, null);
    }
  };

  const handleSendMessage = (draftMessage: DraftMessage) => {
    if (!id) return;
    log.info('addMessage to existing conversation: ', draftMessage);
    addMessage({ ...draftMessage, conversationId: id, channelType });
  };

  const handleCloseSearch = () => {
    setSearchBox(false);
    setSearchValue('');
  };

  const handleChannelChange = (channel: ChannelTypeEnum) => {
    setChannelType(channel);
    setIsUserChannelChoose(true);
  };

  useEffect(() => {
    if (
      lastMessageChannelType &&
      activeChannels.length > 0 &&
      !isUserChannelChoose
    ) {
      setChannelType(
        activeChannels.includes(lastMessageChannelType)
          ? lastMessageChannelType
          : activeChannels[0]
      );
    }
  }, [
    lastMessageChannelType,
    activeChannels,
    setChannelType,
    isUserChannelChoose,
  ]);

  useEffect(() => {
    dispatch(actions.setHiddenBottomNav(true));

    return () => {
      dispatch(actions.setHiddenBottomNav(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (id) dispatch(actions.fetchConversationById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (errorMessage) {
      lib.ToastMessage('error', errorMessage);
      setErrorMessage(null);
    }
  }, [errorMessage, setErrorMessage, t]);

  return (
    <Container>
      <NuacomComponent.Header {...props}>
        {!searchBox ? (
          <>
            <TitleContent>
              <lib.ReactSVG
                src={ArrowLeftIcon}
                className="icon-arrow-left"
                data-test-id="messages-back-button"
                onClick={() => navigate.conversations()}
              />

              <span
                data-test-id="messages-contact-name"
                title={contactNumber}
                onDoubleClick={() =>
                  log.debug('Conversation Info: ', activeChat)
                }>
                {displayContactTitle}
              </span>

              {!isLoading && hasChannels && (
                <ChannelIconStack data-test-id="messages-channel-icon">
                  {channels.map(
                    (channel, index) =>
                      (
                        <lib.ReactSVG
                          key={channel}
                          className={
                            index === 0 ? 'primary-icon' : 'secondary-icon'
                          }
                          src={getChannelIconById(channel)}
                        />
                      )!
                  )}
                </ChannelIconStack>
              )}
            </TitleContent>
            <Right>
              <IconButton
                size="small"
                className="icon-actions"
                title="Call"
                aria-label="Call"
                data-test-id="messages-call-button"
                onClick={handleCallAction}>
                <lib.ReactSVG src={CallConversationIcon} />
              </IconButton>
              <IconButton
                size="small"
                className="icon-actions"
                title="Search"
                aria-label="Search"
                data-test-id="messages-search-button"
                onClick={() => setSearchBox(true)}>
                <lib.ReactSVG src={SearchConversationIcon} />
              </IconButton>
            </Right>
          </>
        ) : (
          <SearchBox>
            <SearchInput
              id="search-in-messages"
              data-test-id="messages-search-input"
              name="search-in-messages"
              placeholder={t('screens.conversation.messages.searchInMessages')}
              autoFocus
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchValue(e.target.value)
              }
            />
            <ClearSearchBtn
              data-test-id="messages-clear-search"
              onClick={handleCloseSearch}>
              <Close fontSize="inherit" />
            </ClearSearchBtn>
          </SearchBox>
        )}
      </NuacomComponent.Header>

      {isLoading ? (
        <Loader>
          <Loaders.List numRows={11} />
        </Loader>
      ) : (
        <Block>
          <MessageList data={messages} searchValue={searchValue} />
          {isWhatsAppResponseWindowClose && (
            <ResponseWindowClosed data-test-id="ttr-expired-banner">
              {t('The response window is closed.')}
            </ResponseWindowClosed>
          )}
          {activeChannelsOrdered.length > 0 ? (
            <MessageTextInput
              onSend={handleSendMessage}
              channelType={channelType}
              channelOptions={activeChannelsOrdered}
              onChannelChange={handleChannelChange}
              isLoading={loading}
              isExpired={isWhatsAppResponseWindowClose}
              {...(isWhatsAppResponseWindowClose && {
                placeholder:
                  'You can still reply by selecting a WhatsApp template message.',
                placeholderFontSize: '12px',
                indicateTemplateRequired: true,
              })}
            />
          ) : (
            <ResponseWindowClosed data-test-id="no-channels-available-banner">
              {t(
                'This channel is disabled for your account. You can only read messages.'
              )}
            </ResponseWindowClosed>
          )}
        </Block>
      )}
    </Container>
  );
}
